"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

export default function DashboardSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#0B0F14]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Dashboard Mockup */}
          <div className="reveal">
            <div
              className="bg-[#11161D] rounded-2xl border border-[rgba(0,230,118,0.15)] overflow-hidden"
              style={{ boxShadow: "0 0 40px rgba(0,230,118,0.06)" }}
            >
              {/* Window Bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[rgba(0,230,118,0.08)] bg-[#0B0F14]">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[rgba(255,255,255,0.08)]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[rgba(255,255,255,0.08)]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[rgba(255,255,255,0.08)]" />
                </div>
                <span className="text-[#6B7A8D] font-mono text-[10px] tracking-[0.15em] uppercase">
                  ScaledOut · Dashboard
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#00E676]" />
                  <span className="text-[#00E676] font-mono text-[10px]">LIVE</span>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="p-6 grid grid-cols-2 gap-3">
                {[
                  { label: "Accounts Active", value: "1,248", trend: "+12" },
                  { label: "Monthly Volume", value: "$25.4M", trend: "+8.2%" },
                  { label: "Top-Up Speed", value: "<15 min", trend: "avg" },
                  { label: "Uptime", value: "99.2%", trend: "30d" },
                ]?.map((metric) => (
                  <div
                    key={metric?.label}
                    className="bg-[#0B0F14] rounded-xl p-4 border border-[rgba(0,230,118,0.06)]"
                  >
                    <div className="text-[#6B7A8D] text-[10px] uppercase tracking-[0.12em] font-bold mb-2">
                      {metric?.label}
                    </div>
                    <div className="text-[#F0F4F8] font-mono font-bold text-lg">
                      {metric?.value}
                    </div>
                    <div className="text-[#00E676] text-[10px] font-mono mt-1">
                      {metric?.trend}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status Bar */}
              <div className="px-6 pb-5">
                <div className="bg-[#0B0F14] rounded-xl p-4 border border-[rgba(0,230,118,0.06)]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#6B7A8D] text-[10px] uppercase tracking-[0.12em] font-bold">
                      Infrastructure Health
                    </span>
                    <span className="text-[#00E676] font-mono text-[10px]">NOMINAL</span>
                  </div>
                  <div className="h-1.5 bg-[rgba(255,255,255,0.04)] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: "99.2%",
                        background:
                          "linear-gradient(90deg, #00E676, #00ff88)",
                        boxShadow: "0 0 8px rgba(0,230,118,0.6)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Text */}
          <div className="reveal reveal-delay-2">
            <div className="tag-badge mb-6">Operational Infrastructure</div>
            <h2 className="section-title text-[#F0F4F8] mb-8">
              Agency-Grade{" "}
              <span className="text-[#00E676]">Operations.</span>
            </h2>
            <div className="space-y-4">
              {[
                "Agency-grade Meta ad accounts",
                "Verified operational structure",
                "24/7 capital processing",
                "Stability monitoring protocols",
              ]?.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#00E676] flex-shrink-0"
                    style={{ boxShadow: "0 0 6px rgba(0,230,118,0.8)" }}
                  />
                  <span className="text-[#6B7A8D] text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}